import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTimer } from './use-timer';
interface UseHistoryStateConfig<T> {
  size?: number;
  overrideLastItemDebounceMS?: number;
  onChange?: (data: T) => void;
}
const defaultConfig = {
  size: 20,
  overrideLastItemDebounceMS: -1 // overriding is disabled by default
};
interface SetStateConfig {
  /**
   * Update last history element (not create new one)
   */
  overrideLastItem?: boolean;
}
type HistoryActionResult<T> = {
  success: false;
} | {
  success: true;
  value: T;
};
export function useHistoryState(initialValue, config) {
  const $ = _c(25);
  let t0;
  if ($[0] !== config) {
    t0 = {
      ...defaultConfig,
      ...config
    };
    $[0] = config;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    size,
    overrideLastItemDebounceMS,
    onChange
  } = t0;
  const [state, _setState] = useState(initialValue);
  let t1;
  if ($[2] !== state) {
    t1 = [state];
    $[2] = state;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [history, setHistory] = useState(t1);
  const [pointer, setPointer] = useState(0);
  const timer = useTimer();
  let t2;
  if ($[4] !== history || $[5] !== onChange || $[6] !== overrideLastItemDebounceMS || $[7] !== pointer || $[8] !== size || $[9] !== state || $[10] !== timer) {
    t2 = (value, config_0) => {
      if (typeof value === "object" && Object.entries(value).toString() === Object.entries(state).toString() || value === state) {
        return;
      }
      const isOverridingEnabled = overrideLastItemDebounceMS > 0;
      const timePassedSinceLastChange = timer.check();
      const debounceTimePassed = isOverridingEnabled && timePassedSinceLastChange !== undefined ? timePassedSinceLastChange > overrideLastItemDebounceMS : true;
      const shouldOverrideLastItem = config_0?.overrideLastItem !== undefined ? config_0.overrideLastItem : !debounceTimePassed;
      if (shouldOverrideLastItem) {
        setHistory(prev => [...prev.slice(0, pointer), value]);
      } else {
        const isSizeOverflow = history.length >= size;
        setHistory(prev_0 => [...prev_0.slice(isSizeOverflow ? 1 : 0, pointer + 1), value]);
        if (!isSizeOverflow) {
          setPointer(_temp);
        }
      }
      _setState(value);
      onChange?.(value);
    };
    $[4] = history;
    $[5] = onChange;
    $[6] = overrideLastItemDebounceMS;
    $[7] = pointer;
    $[8] = size;
    $[9] = state;
    $[10] = timer;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const setState = t2;
  let t3;
  if ($[12] !== history || $[13] !== onChange || $[14] !== pointer) {
    t3 = () => {
      if (pointer <= 0) {
        return {
          success: false
        };
      }
      const value_0 = history[pointer - 1];
      _setState(value_0);
      setPointer(_temp2);
      onChange?.(value_0);
      return {
        success: true,
        value: value_0
      };
    };
    $[12] = history;
    $[13] = onChange;
    $[14] = pointer;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const undo = t3;
  let t4;
  if ($[16] !== history || $[17] !== onChange || $[18] !== pointer) {
    t4 = () => {
      if (pointer + 1 >= history.length) {
        return {
          success: false
        };
      }
      const value_1 = history[pointer + 1];
      _setState(value_1);
      setPointer(_temp3);
      onChange?.(value_1);
      return {
        success: true,
        value: value_1
      };
    };
    $[16] = history;
    $[17] = onChange;
    $[18] = pointer;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const redo = t4;
  let t5;
  if ($[20] !== redo || $[21] !== setState || $[22] !== state || $[23] !== undo) {
    t5 = [state, setState, undo, redo];
    $[20] = redo;
    $[21] = setState;
    $[22] = state;
    $[23] = undo;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5 as const;
}
function _temp3(prev_3) {
  return prev_3 + 1;
}
function _temp2(prev_2) {
  return prev_2 - 1;
}
function _temp(prev_1) {
  return prev_1 + 1;
}